import { useCallback, useRef } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { ProviderSummary } from '../domain/types';
import { useAsyncResource } from './use-async-resource';

export interface UseProviderSummariesReturn {
  summaries: ProviderSummary[];
  isLoading: boolean;
  hasError: boolean;
  /** Re-runs the fetch and tells the adapter to skip its cache. */
  refetch: () => void;
}

const EMPTY_SUMMARIES: ProviderSummary[] = [];

/**
 * Provider roll-ups for the overview. Not polled: the adapter caches the
 * fan-out for a minute and a refresh is an explicit operator action.
 */
export const useProviderSummaries = (): UseProviderSummariesReturn => {
  const client = usePartnerDataClient();
  // Flipped by `refetch` so the very next fetch bypasses the adapter cache,
  // then reset so a later re-render does not keep hammering PDGS.
  const bypassCacheRef = useRef(false);

  const fetchSummaries = useCallback(() => {
    const bypassCache = bypassCacheRef.current;
    bypassCacheRef.current = false;

    return client.listProviderSummaries({ bypassCache });
  }, [client]);

  const { data, isLoading, hasError, refetch } = useAsyncResource(
    fetchSummaries,
    EMPTY_SUMMARIES,
  );

  const refetchBypassingCache = (): void => {
    bypassCacheRef.current = true;
    refetch();
  };

  return {
    summaries: data,
    isLoading,
    hasError,
    refetch: refetchBypassingCache,
  };
};
